import {FC, useCallback, useEffect, useState} from "react";
import styled from "styled-components";
import {useTranslation} from "react-i18next";
import {Collapse} from "@mui/material";
import {IAchievement} from "modules/reducers";
import {useIsMobile} from "modules/utils";
import {IBadgesFilters} from "modules/types";
import {BadgesListItem, BadgesProgress, Exist, PureButton} from "components";
import {ReactComponent as Arrow} from "assets/images/icons/small-arrow.svg";
import {get} from "lodash";

const Wrapper = styled.div`
	width: 100%;
	padding: 40px 20px;
	background: #2c2c2e;

	@media (min-width: 769px) {
		background: #2c2c2ecc;
		padding: 44px 20px;
	}
`;

const Container = styled.div`
	display: grid;
	grid-template-rows: auto;
	grid-template-columns: repeat(auto-fill, minmax(102px, 1fr));
	grid-row-gap: 32px;
	grid-column-gap: 14px;
	margin-top: 28px;

	@media (min-width: 769px) {
		grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
		margin-top: 54px;
	}
`;

const Info = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;

	@media (min-width: 769px) {
		gap: 30px;
		flex-direction: row;
		justify-content: space-between;
	}
`;

const Flex = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
`;

const TitleContainer = styled.div`
	display: flex;
	flex-direction: column;
	font-family: "Khand", sans-serif;
	font-weight: 700;
	line-height: 130%;
	margin-right: 16px;
	text-transform: uppercase;
	gap: 4px;
`;

const Title = styled.div`
	font-size: 30px;
	line-height: 100%;
`;

const SubTitle = styled.div`
	font-size: 12px;

	@media (min-width: 769px) {
		font-size: 14px;
	}
`;

const ProgressContainer = styled.div`
	max-width: 268px;
	width: 100%;
`;

const Button = styled(PureButton)<{isOpen: boolean}>`
	width: 24px;
	height: 24px;
	transition: all 0.3s ease-in-out;
	transform: rotate(${({isOpen}) => (isOpen ? "180" : "0")}deg);
`;

const subTitles = {
	pickem: "badges.game_sub_title.pickem",
};

interface IBadgesGameList {
	achievements: IAchievement[];
	game: string;
	filters: IBadgesFilters;
}

export const BadgesGameList: FC<IBadgesGameList> = ({achievements, game, filters}) => {
	const {t} = useTranslation();
	const {isMobile} = useIsMobile();
	const [isOpen, setIsOpen] = useState(true);
	const count = achievements.filter((achievement) => achievement.earned).length;
	const isOpenCollapse = isMobile ? isOpen : true;
	const subTitle = get(subTitles, game, "") as string;

	useEffect(() => {
		if (filters.game) {
			setIsOpen(filters.game === game);
		} else {
			setIsOpen(true);
		}
	}, [filters.game, game]);

	const toggleIsOpen = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, [setIsOpen]);

	return (
		<Wrapper>
			<Exist when={!isMobile}>
				<Info>
					<TitleContainer>
						<Title>{t(`badges.game.${game}`)}</Title>
						<SubTitle>{t(subTitle)}</SubTitle>
					</TitleContainer>

					<ProgressContainer>
						<BadgesProgress count={count} total={achievements.length} />
					</ProgressContainer>
				</Info>
			</Exist>

			<Exist when={isMobile}>
				<Info>
					<Flex>
						<TitleContainer>
							<Title>{t(`badges.game.${game}`)}</Title>
							<SubTitle>{t(subTitle)}</SubTitle>
						</TitleContainer>

						<Button onClick={toggleIsOpen} isOpen={isOpen}>
							<Arrow />
						</Button>
					</Flex>

					<ProgressContainer>
						<BadgesProgress count={count} total={achievements.length} />
					</ProgressContainer>
				</Info>
			</Exist>

			<Collapse in={isOpenCollapse}>
				<Container>
					{achievements.map((achievement, i) => (
						<BadgesListItem key={`${achievement.name}-${i}`} {...achievement} />
					))}
				</Container>
			</Collapse>
		</Wrapper>
	);
};
